import React, {ComponentType} from 'react';

import {
  DocumentPrivateStatusButton,
  DocumentSequencedStepsStatusButton,
  DocumentSubscribedStatusButton,
  DocumentSubscribersButton,
} from 'app/general/doc-status';

import type {
  ContentType,
  DetailPolicySerializedType,
  KnowledgebaseSerializedType,
  ProcedureSerializedType,
  ProcessSerializedType,
  QuizSerializedType,
  TaskInstanceSerializedType,
} from 'sweet-types';

type TaskAssignedDocument =
  TaskInstanceSerializedType['template']['assigned_object'];

interface ButtonComponentProp<T, CT> {
  document: T;
  canEdit: boolean;
  contentType: CT;
}
type ContextType = 'task' | 'view' | 'edit';

const PRIVATE_CTS: Array<'procedure' | 'policy' | 'proceduregroup'> = [
  'procedure',
  'policy',
  'proceduregroup',
];

const AVAILABLE_STATUSES: Array<
  Array<{
    name: string;
    cts: Array<ContentType>;
    whitelistContext: Array<ContextType>;
    Component: ComponentType<ButtonComponentProp<any, any>>;
  }>
> = [
  [
    {
      name: 'subscribed',
      cts: ['procedure', 'policy', 'proceduregroup', 'knowledgebase'],
      whitelistContext: ['view', 'edit'],
      Component: DocumentSubscribedStatusButton,
    },
    {
      name: 'subscribers',
      cts: ['procedure', 'policy', 'proceduregroup', 'knowledgebase'],
      whitelistContext: ['view', 'edit'],
      Component: DocumentSubscribersButton,
    },
  ],
  [
    {
      name: 'sequenced',
      whitelistContext: ['view', 'edit', 'task'],
      cts: ['procedure', 'proceduregroup'],
      Component: function SequencedIconStatus({
        document,
        canEdit,
        contentType,
      }: ButtonComponentProp<
        ProcedureSerializedType | ProcessSerializedType | TaskAssignedDocument,
        'procedure' | 'proceduregroup'
      >) {
        return (
          <DocumentSequencedStepsStatusButton
            contentType={contentType}
            // task-assigned document has no URL, so the button will be read-only
            documentURL={'url' in document ? document.url : ''}
            canEdit={'url' in document ? canEdit : false}
            isSequenced={document.steps_blocked_at != null}
          />
        );
      },
    },
    {
      name: 'private',
      whitelistContext: ['view', 'edit'],
      cts: PRIVATE_CTS,
      Component: function SequencedIconStatus({
        document,
        contentType,
        canEdit,
      }: ButtonComponentProp<
        | ProcedureSerializedType
        | DetailPolicySerializedType
        | ProcessSerializedType,
        'procedure' | 'policy' | 'proceduregroup'
      >) {
        return (
          <DocumentPrivateStatusButton
            object={document}
            ct={contentType}
            canEdit={canEdit}
            isPrivate={document.private}
          />
        );
      },
    },
  ],
];

interface Props {
  canEdit: boolean;
  contentType: ContentType;
  document:
    | ProcedureSerializedType
    | DetailPolicySerializedType
    | ProcessSerializedType
    | QuizSerializedType
    | KnowledgebaseSerializedType
    | TaskAssignedDocument;
  context: ContextType;
}
export const DocumentMetadataStatusButtons = ({
  document,
  contentType,
  context,
  canEdit,
}: Props): JSX.Element => {
  return (
    <>
      {AVAILABLE_STATUSES.map((buttons, i) => (
        <div key={i} className="btn-group ml-2 d-print-none" role="group">
          {buttons.map(({name, cts, whitelistContext, Component}) =>
            cts.indexOf(contentType) > -1 &&
            whitelistContext.indexOf(context) > -1 ? (
              <Component
                canEdit={canEdit}
                key={name}
                document={document}
                contentType={contentType}
              />
            ) : null
          )}
        </div>
      ))}
    </>
  );
};
